import type { OrgToolLinksStored } from "@/lib/locationToolLinks";
import {
  ORG_LIFECYCLE_BASE,
  ORG_LOCATION_TOOL_LINKS_BASE,
  SOCIAL_MAPPINGS_BASE,
  adsAssistantClientFetch,
  type AdsAssistantFetchResult,
} from "./adsAssistantClientHttp";
import type { AdsLocationSocialMapping, AdsSocialDefaults } from "./adsAssistantClientTypes";

export async function fetchAdsSocialMappings(): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    can_edit: boolean;
    organization: AdsSocialDefaults | null;
    locations: AdsLocationSocialMapping[];
  }>
> {
  return adsAssistantClientFetch(SOCIAL_MAPPINGS_BASE, { method: "GET" });
}

export async function updateAdsSocialDefaults(patch: {
  social_default_facebook_page_url?: string | null;
  social_default_facebook_page_id?: string | null;
  social_default_google_business_url?: string | null;
  social_default_google_business_location_id?: string | null;
}): Promise<AdsAssistantFetchResult<{ ok: true; target: "organization" }>> {
  return adsAssistantClientFetch(SOCIAL_MAPPINGS_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target: "organization", ...patch }),
  });
}

export async function updateAdsLocationSocialMapping(
  location_id: string,
  patch: {
    social_facebook_page_url?: string | null;
    social_facebook_page_id?: string | null;
    social_google_business_url?: string | null;
    social_google_business_location_id?: string | null;
  }
): Promise<AdsAssistantFetchResult<{ ok: true; target: "location"; location_id: string }>> {
  return adsAssistantClientFetch(SOCIAL_MAPPINGS_BASE, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target: "location", location_id, ...patch }),
  });
}

/** GET `/api/ads-assistant/org-location-tool-links` — workspace org from `X-Workspace-Organization-Id` + JWT. */
export async function getOrgLocationToolLinks(): Promise<
  AdsAssistantFetchResult<{
    ok: true;
    payload: OrgToolLinksStored | null;
    updated_at: string | null;
    updated_by: string | null;
    can_edit: boolean;
  }>
> {
  return adsAssistantClientFetch(ORG_LOCATION_TOOL_LINKS_BASE, { method: "GET" });
}

export async function putOrgLocationToolLinks(
  payload: OrgToolLinksStored
): Promise<AdsAssistantFetchResult<{ ok: true }>> {
  return adsAssistantClientFetch(ORG_LOCATION_TOOL_LINKS_BASE, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
  });
}

/** POST same base as org tool links — creates a `locations` row for the workspace org (service role). */
export async function postOrgAddLocation(name: string): Promise<
  AdsAssistantFetchResult<{ ok: true; location: { id: string; name: string } }>
> {
  return adsAssistantClientFetch(ORG_LOCATION_TOOL_LINKS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
}

/** POST `{ deleteLocationId }` — removes a location (org admin / owner / platform); cleans per-location link overrides. */
export async function postOrgDeleteLocation(
  deleteLocationId: string
): Promise<AdsAssistantFetchResult<{ ok: true; deletedLocationId: string }>> {
  return adsAssistantClientFetch(ORG_LOCATION_TOOL_LINKS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deleteLocationId }),
  });
}

/** GET — whether the signed-in user may soft-deactivate the workspace org. */
export async function getOrgLifecycleDeactivateEligibility(): Promise<
  AdsAssistantFetchResult<{ ok: true; can_deactivate: boolean }>
> {
  return adsAssistantClientFetch(ORG_LIFECYCLE_BASE, { method: "GET" });
}

/** POST — soft-deactivate workspace org (`is_active = false`). Platform admin/dev may pass `transfer_to_organization_id`. */
export async function postOrgDeactivate(body: {
  transfer_to_organization_id?: string | null;
}): Promise<AdsAssistantFetchResult<{ ok: true; already_inactive?: boolean }>> {
  return adsAssistantClientFetch(ORG_LIFECYCLE_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}
